import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import PublicStructuredDataManager from "components/PublicStructuredDataManager";
import SiteFooter from "components/SiteFooter";
import NotFound from "pages/NotFound";
import Authentication from './pages/authentication';
import Analytics from './pages/analytics';
import FormBuilder from './pages/form-builder';
import AccountSettings from './pages/account-settings';
import TemplatesGallery from './pages/templates-gallery';
import EmbedSettings from './pages/embed-settings';
import Dashboard from './pages/dashboard';
import SubmissionsManagement from './pages/submissions-management';
import ABTestingDashboard from './pages/a-b-testing-dashboard';
import AccountManagementConsole from './pages/account-management-console';
import PricingPlansManagement from './pages/pricing-plans-management';
import HomePage from './pages/home-page';
import PaymentProcessorsManagement from './pages/payment-processors-management';
import SubscriptionManagementDashboard from './pages/subscription-management-dashboard';
import PaymentFormBuilder from './pages/payment-form-builder';
import HelpCenterHub from './pages/help-center-hub';
import KnowledgeBaseArticles from './pages/knowledge-base-articles';
import SupportTicketSystem from './pages/support-ticket-system';
import ApiReference from './pages/api-reference';
import UserAnalyticsDashboard from './pages/user-analytics-dashboard';
import PublishScreen from './pages/publish-screen';
import SuperAdminDashboard from './pages/super-admin-dashboard';
import SuperAdminLogin from './pages/super-admin-login';
import AdminOnboarding from './pages/admin-onboarding';
import WhiteLabelBranding from './pages/white-label-branding';
import ClientWorkspaces from './pages/client-workspaces';
import PublicFormViewer from './pages/public-form-viewer';
import ExperimentRouter from './pages/experiment-router';
import TestUserManagement from './pages/test-user-management';
import A2PConsentManagement from './pages/a2p-consent-management';
import WhiteLabelConfiguration from './pages/white-label-configuration';
import WhiteLabelCustomerPortal from './pages/white-label-customer-portal';
import PortalSubmissionsDashboard from './pages/portal-submissions-dashboard';
import PortalAnalyticsDashboard from './pages/portal-analytics-dashboard';
import WhiteLabelAccounts from './pages/white-label-accounts';
import WhiteLabelPreview from './pages/white-label-preview';
import SignupPage from './pages/signup';
import TermsOfService from './pages/terms';
import PrivacyPolicy from './pages/privacy';
import ResetPassword from './pages/reset-password';
import ProtectedRoute from './components/ProtectedRoute';
import {
  AboutPage,
  CustomersPage,
  FeaturesPage,
  JotformComparisonPage,
  PaperformComparisonPage,
  PricingPage,
  TallyComparisonPage,
  TypeformComparisonPage,
} from './pages/marketing-content';

const LegacyAnalyticsRedirect = ({ tab }) => {
  const location = useLocation();
  const { formId } = useParams();
  const params = new URLSearchParams(location.search);

  params.set('tab', tab);
  if (formId) params.set('id', formId);

  return <Navigate to={`/analytics?${params.toString()}`} replace />;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <PublicStructuredDataManager />
      <RouterRoutes>
        {/* ── Public routes ─────────────────────────────────────── */}
        <Route path="/" element={<HomePage />} />
        <Route path="/home-page" element={<HomePage />} />
        <Route path="/authentication" element={<Authentication />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        {/* Public marketing pages have their own content and crawlable metadata. */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/vs/jotform" element={<JotformComparisonPage />} />
        <Route path="/vs/typeform" element={<TypeformComparisonPage />} />
        <Route path="/vs/paperform" element={<PaperformComparisonPage />} />
        <Route path="/vs/tally" element={<TallyComparisonPage />} />
        <Route path="/experiments/:testId" element={<ExperimentRouter />} />

        {/* ── Authenticated routes ───────────────────────────────── */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/form-analytics/:formId" element={<ProtectedRoute><LegacyAnalyticsRedirect tab="forms" /></ProtectedRoute>} />
        <Route path="/form-analytics" element={<ProtectedRoute><LegacyAnalyticsRedirect tab="forms" /></ProtectedRoute>} />
        <Route path="/form-builder" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
        <Route path="/account-settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
        <Route path="/templates-gallery" element={<TemplatesGallery />} />
        <Route path="/templates/:templateSlug" element={<TemplatesGallery />} />
        <Route path="/embed-settings" element={<ProtectedRoute><EmbedSettings /></ProtectedRoute>} />
        <Route path="/submissions-management" element={<ProtectedRoute><SubmissionsManagement /></ProtectedRoute>} />
        {/* Removed legacy feature surfaces; keep old bookmarks safe. */}
        <Route path="/form-automations" element={<Navigate to="/dashboard" replace />} />
        <Route path="/email-automation-sequences" element={<Navigate to="/dashboard" replace />} />
        <Route path="/email-template-builder" element={<Navigate to="/dashboard" replace />} />
        <Route path="/email-notification-templates" element={<Navigate to="/dashboard" replace />} />
        <Route path="/a-b-testing-dashboard" element={<ProtectedRoute><ABTestingDashboard /></ProtectedRoute>} />
        <Route path="/payment-form-builder" element={<ProtectedRoute><PaymentFormBuilder /></ProtectedRoute>} />
        <Route path="/payment-processors-management" element={<ProtectedRoute><PaymentProcessorsManagement /></ProtectedRoute>} />
        <Route path="/subscription-management-dashboard" element={<ProtectedRoute><SubscriptionManagementDashboard /></ProtectedRoute>} />
        <Route path="/help-center-hub" element={<ProtectedRoute><HelpCenterHub /></ProtectedRoute>} />
        <Route path="/knowledge-base-articles" element={<ProtectedRoute><KnowledgeBaseArticles /></ProtectedRoute>} />
        <Route path="/support-ticket-system" element={<ProtectedRoute><SupportTicketSystem /></ProtectedRoute>} />
        <Route path="/api-reference" element={<ProtectedRoute><ApiReference /></ProtectedRoute>} />
        <Route path="/user-analytics-dashboard" element={<ProtectedRoute requireAdmin><UserAnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/activity-hub" element={<ProtectedRoute><LegacyAnalyticsRedirect tab="activity" /></ProtectedRoute>} />
        <Route path="/event-analytics" element={<ProtectedRoute><LegacyAnalyticsRedirect tab="overview" /></ProtectedRoute>} />
        <Route path="/publish-screen" element={<ProtectedRoute><PublishScreen /></ProtectedRoute>} />
        <Route path="/white-label-branding" element={<ProtectedRoute><WhiteLabelBranding /></ProtectedRoute>} />
        <Route path="/client-workspaces" element={<ProtectedRoute><ClientWorkspaces /></ProtectedRoute>} />
        <Route path="/email-provider-config" element={<Navigate to="/dashboard" replace />} />
        <Route path="/white-label-configuration" element={<ProtectedRoute><WhiteLabelConfiguration /></ProtectedRoute>} />
        <Route path="/white-label-customer-portal" element={<ProtectedRoute><WhiteLabelCustomerPortal /></ProtectedRoute>} />
        <Route path="/portal-submissions-dashboard" element={<ProtectedRoute><PortalSubmissionsDashboard /></ProtectedRoute>} />
        <Route path="/portal-analytics-dashboard" element={<ProtectedRoute><PortalAnalyticsDashboard /></ProtectedRoute>} />
        <Route path="/white-label-accounts" element={<ProtectedRoute><WhiteLabelAccounts /></ProtectedRoute>} />
        <Route path="/white-label-preview" element={<ProtectedRoute><WhiteLabelPreview /></ProtectedRoute>} />

        {/* ── Admin-only routes ──────────────────────────────────── */}
        <Route path="/super-admin-login" element={<SuperAdminLogin />} />
        <Route path="/super-admin-dashboard" element={<ProtectedRoute requireAdmin><SuperAdminDashboard /></ProtectedRoute>} />
        {/* admin-onboarding must be reachable by authenticated non-admins
            for the first-owner bootstrap flow; the page itself handles
            authorization via the bootstrap_admin_owner() SECURITY DEFINER RPC */}
        <Route path="/admin-onboarding" element={<ProtectedRoute><AdminOnboarding /></ProtectedRoute>} />
        <Route path="/account-management-console" element={<ProtectedRoute requireAdmin><AccountManagementConsole /></ProtectedRoute>} />
        <Route path="/pricing-plans-management" element={<ProtectedRoute requireAdmin><PricingPlansManagement /></ProtectedRoute>} />
        <Route path="/test-user-management" element={<ProtectedRoute requireAdmin><TestUserManagement /></ProtectedRoute>} />
        <Route path="/a2p-consent-management" element={<ProtectedRoute requireAdmin><A2PConsentManagement /></ProtectedRoute>} />

        {/* ── Public form viewer — matches wf_ prefixed form IDs ── */}
        <Route path="/:formId" element={<PublicFormViewer />} />
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      <SiteFooter />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
